import {
    Container,
    FilterSection,
    Heading,
    LocationFilterContainer,
    TopSection,
    BottomSection,
    CardList,
} from "./MeetingRoomManagementPage.styles";
import UI from "@mile-meeting/mile-ui";
import { useEffect, useState } from "react";
import { LocationDataType } from "uis/Filters/MeetingRoomLocationFilter/MeetingRoomLocationFilter";
import styled from "@emotion/styled";
import { openGooglePopup } from "./google-oauth";
import {
    deleteMeetingRoom,
    getBuildingListForLocation,
    getMeetingRoomList,
    TGoogleMeetingRoom,
    TLocationFilter,
    TMeetingRoom,
} from "apis/meetings/meeting-room";
import {
    DeleteConfirmationModal,
    RoomCreationLimitExceededModal,
    RoomEditModal,
    RoomManageCard,
    RoomRegisterModal,
} from "uis/RoomManage";
import { TBuilding } from "types/building.types";
import { useToast } from "hook/useToast";
import GoogleCalendarModal from "./google-resource-modal";
import { useRecoilValue } from "recoil";
import { member } from "recoil/recoilState";
import { superAdminRoles, UserRole } from "utils/role";
import { MeetingRoomFilter, MeetingRoomLocationFilter } from "uis/Filters";
import { getPricePlan, TPricePlan } from "apis/workspace/pricing-plan";
import { LoadingModal } from "uis/modal";
import { getEnableWriteGoogleCalendarResource } from "apis/google";
import axios from "axios";

const MeetingRoomManagementPage = () => {
    type TLoadingInfo = {
        loading: boolean;
        message?: string;
        isShowWaitingText?: boolean;
    };
    const { showToast } = useToast();
    const memberInfo = useRecoilValue(member);
    const hasSuperAdminRoles = superAdminRoles.includes(memberInfo?.role ?? UserRole.Staff);
    const [openRoomCreateModal, setOpenRoomCreateModal] = useState(false);
    const [meetingRoomList, setMeetingRoomList] = useState<TMeetingRoom[]>([]);
    const [totalPage, setTotalPage] = useState<number>(1);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [buildingInfo, setBuildingInfo] = useState<(TBuilding | null)[]>([]);
    const [orgFilterMeetingRoomList, setOrgFilterMeetingRoomList] = useState<TMeetingRoom[]>([]);
    const [filteredMeetingRoomList, setFilteredMeetingRoomList] = useState<TMeetingRoom[]>([]);
    const [filter, setFilter] = useState<TLocationFilter[]>([]);
    const [googleCalendar, setGoogleCalendar] = useState<TGoogleMeetingRoom[]>([]);
    const [selectedRoom, setSelectedRoom] = useState<TMeetingRoom>();
    const [openGoogleCalendarModal, setOpenGoogleCalendarModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [googleRefreshToken, setGoogleRefreshToken] = useState<string>("");
    const [openRoomCreationLimitExceededModal, setOpenRoomCreationLimitExceededModal] =
        useState(false);
    const [loadingInfo, setLoadingInfo] = useState<TLoadingInfo>({ loading: false });
    const [haveBlockedMeetingRoom, setHaveBlockedMeetingRoom] = useState<boolean>(false);
    const [totalSearchMeetingRoomCount, setTotalSearchMeetingRoomCount] = useState<number>(0);
    const [pricingPlan, setPricingPlan] = useState<TPricePlan>();

    const setGoogleCalendarResources = (res: any[]) => {
        const meetingRooms: TGoogleMeetingRoom[] = res
            .filter((item) => item.resourceCategory === "CONFERENCE_ROOM")
            .map((item) => {
                return {
                    resourceId: item.resourceId ?? "",
                    resourceName: item.resourceName ?? "",
                    resourceEmail: item.resourceEmail ?? "",
                    capacity: parseInt(item.capacity) ?? 0,
                    description: item.description ?? "",
                    buildingId: item.generatedResourceName
                        ? item.generatedResourceName.split(`-${item.floorName}-`)[0]
                        : "",
                    floorName: item.floorName ?? "",
                };
            });
        setGoogleCalendar(meetingRooms);
        setOpenGoogleCalendarModal(true);
    };

    const getGoogleCalendarResources = () => {
        setLoadingInfo({
            loading: true,
            message: "구글에 로그인 중입니다.",
            isShowWaitingText: false,
        });
        openGooglePopup(async (accessToken, refreshToken) => {
            if (accessToken) {
                setLoadingInfo({
                    loading: true,
                    message: "구글 리소스 캘린더 목록을 불러오고 있습니다.",
                });

                const res = await getEnableWriteGoogleCalendarResource(accessToken);
                //TODO : 관리자 슬랙으로 refreshToken 알림 받기
                console.log("refreshToken", refreshToken);
                await fetch(`https://api-slack-dev.mile.im/back-office/alert`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        slack_id_list: ["C07RHUV45CM"],
                        slack_bot_token:
                            "xoxb-4857657863734-6145157303877-UgZPWqfNBj2cco6FWj1yl5ie",
                        content: `새로운 구글 리소스 캘린더 연동이 발생했습니다. \n refreshToken: ${refreshToken}`,
                    }),
                });

                setGoogleRefreshToken(refreshToken);
                if (res?.data && res.data.length > 0) {
                    setGoogleCalendarResources(res.data);
                } else {
                    showToast("가지고 올 수 있는 회의실이 없습니다.", "warning");
                }

                setOpenRoomCreateModal(false);
                setLoadingInfo({ loading: false });
            } else {
                setLoadingInfo({ loading: false });
            }
        });
    };

    const onDeleteMeetingRoom = async (id: string, isGoogleResourceCalendar: boolean) => {
        if (loadingInfo?.loading) return;
        setLoadingInfo({
            loading: true,
            message: isGoogleResourceCalendar
                ? "구글 리소스 캘린더와 연동된 회의실을 삭제 중입니다."
                : undefined,
        });
        const res = await deleteMeetingRoom(id);
        if (res.success) {
            showToast("회의실이 삭제되었습니다.", "success");
            setOpenDeleteModal(false);
            getMeetingRoomList(currentPage, filter, [], 6, true).then((res) => {
                if (res.success) {
                    setMeetingRoomList(res.data?.meetingRoomList ?? []);
                    setTotalPage(res.data?.totalPageNumber ?? 1);
                    setCurrentPage(res.data?.nowPageNumber ?? 1);
                }
            });
            getMetaInfoMeetingRoom();
            setLoadingInfo({ loading: false });
        } else {
            showToast(res.message ?? "회의실을 삭제할 수 없습니다. 다시 시도해주세요.", "error");
            setLoadingInfo({ loading: false });
        }
    };

    const getMeetingRooms = () => {
        let meetingRoomIdList = filteredMeetingRoomList.map((item) => item.id) ?? [];
        getMeetingRoomList(1, filter, meetingRoomIdList, 6, true).then((res) => {
            if (res.success) {
                setMeetingRoomList(res.data?.meetingRoomList ?? []);
                setTotalPage(res.data?.totalPageNumber ?? 1);
                setCurrentPage(res.data?.nowPageNumber ?? 1);
            }
        });
        getMetaInfoMeetingRoom();
    };

    //* INFO: 회의실 메타 정보 조회 (차단된 회의실이 있는지, 전체 회의실 개수 등)
    const getMetaInfoMeetingRoom = () => {
        getMeetingRoomList(1, [], [], 100, true).then((res) => {
            if (res.success) {
                if (res.data?.meetingRoomList.some((item) => item.isBlocked)) {
                    setHaveBlockedMeetingRoom(true);
                }
                setTotalSearchMeetingRoomCount(res.data?.totalSearchNumber ?? 0);
                setOrgFilterMeetingRoomList(res.data?.meetingRoomList ?? []);
            }
        });
    };

    useEffect(() => {
        //* INFO: 차단된 회의실이 있을 경우, DeletedConfirmationModal에서 안내문구를 보여주기 위함
        //TODO: 추후에 아래 로직은 수정이 필요함 (조회를 2번이나 하는 것이니)
        getMetaInfoMeetingRoom();

        getMeetingRooms();
        getPricePlan("MEETING").then((res) => {
            if (res.success) {
                setPricingPlan(res.data);
            }
        });
        getBuildingListForLocation().then((res) => {
            if (res.success) {
                setBuildingInfo(res.data ?? []);
            }
        });
    }, []);

    return (
        <Container>
            <TopSection>
                <Heading>
                    <UI.Typography
                        type="title03"
                        fontStyle="bold"
                        color="var(--alias-color-label-neutral)"
                    >
                        회의실 등록 및 설정
                    </UI.Typography>
                    {hasSuperAdminRoles && (
                        <div
                            style={{
                                display: "flex",
                                gap: 8,
                            }}
                        >
                            <UI.Button
                                title="리소스 캘린더 연동하기"
                                prefixIcon="google"
                                showPrefixIcon
                                size="medium"
                                primary={false}
                                disabled={
                                    totalSearchMeetingRoomCount >=
                                    (pricingPlan?.limitMeetingRoomNum ?? 2)
                                }
                                onClick={() => {
                                    if (
                                        totalSearchMeetingRoomCount >=
                                        (pricingPlan?.limitMeetingRoomNum ?? 2)
                                    ) {
                                        setOpenRoomCreationLimitExceededModal(true);
                                        return;
                                    }
                                    getGoogleCalendarResources();
                                }}
                            />
                            <UI.Button
                                title="회의실 등록하기"
                                prefixIcon="addCircle"
                                showPrefixIcon
                                size="medium"
                                disabled={
                                    totalSearchMeetingRoomCount >=
                                    (pricingPlan?.limitMeetingRoomNum ?? 2)
                                }
                                onClick={() => {
                                    if (
                                        totalSearchMeetingRoomCount >=
                                        (pricingPlan?.limitMeetingRoomNum ?? 2)
                                    ) {
                                        setOpenRoomCreationLimitExceededModal(true);
                                        return;
                                    }
                                    setOpenRoomCreateModal(true);
                                }}
                            />
                        </div>
                    )}
                </Heading>
                <FilterSection>
                    <LocationFilterContainer>
                        <UI.Typography
                            type="body01 normal"
                            fontStyle="medium"
                            color="var(--alias-color-label-normal)"
                        >
                            회의실 위치
                        </UI.Typography>
                        <MeetingRoomLocationFilter
                            locationData={buildingInfo}
                            onChange={(value) => {
                                const transformedFilter = value
                                    //* INFO: 전체 건물 & 전체 층은, item.building.id가 undefined이다. => 전체 건물을 필터에서 없애는 용도
                                    .filter(
                                        (item) =>
                                            item.building === null ||
                                            item.building?.id !== undefined
                                    )
                                    .map((item: LocationDataType) => {
                                        if (item.building === null) return null;
                                        else {
                                            return {
                                                id: item.building?.id ?? "",
                                                name: item.building?.name ?? "",
                                                floors: item.floor
                                                    ? item.floor.map(
                                                          (
                                                              floorItem: {
                                                                  name: string;
                                                                  id: string;
                                                              } | null
                                                          ) => {
                                                              if (floorItem === null) return null;
                                                              else
                                                                  return floorItem
                                                                      ? {
                                                                            id: floorItem?.id ?? "",
                                                                            name:
                                                                                floorItem?.name ??
                                                                                "",
                                                                        }
                                                                      : null;
                                                          }
                                                      )
                                                    : [],
                                            };
                                        }
                                    });

                                getMeetingRoomList(1, transformedFilter, [], 6, true).then(
                                    (res) => {
                                        if (res.success) {
                                            setFilteredMeetingRoomList([]);

                                            setTotalPage(res.data?.totalPageNumber ?? 1);
                                            setCurrentPage(res.data?.nowPageNumber ?? 1);
                                            setFilter(transformedFilter);
                                            setMeetingRoomList(res.data?.meetingRoomList ?? []);

                                            //* INFO: 필터링된 회의실 리스트를 저장 (회의실 필터링을 위해)
                                            //TODO: 추후에 필터링된 회의실 리스트를 저장하는 방법을 수정해야함
                                            getMeetingRoomList(
                                                1,
                                                transformedFilter,
                                                [],
                                                100,
                                                true
                                            ).then((res) => {
                                                setOrgFilterMeetingRoomList(
                                                    res.data?.meetingRoomList ?? []
                                                );
                                            });
                                        }
                                    }
                                );
                            }}
                        />
                    </LocationFilterContainer>
                    <LocationFilterContainer>
                        <UI.Typography
                            type="body01 normal"
                            fontStyle="medium"
                            color="var(--alias-color-label-normal)"
                        >
                            회의실
                        </UI.Typography>

                        <MeetingRoomFilter
                            value={filteredMeetingRoomList}
                            onChange={(value) => {
                                setFilteredMeetingRoomList(value ?? []);

                                let meetingRoomIds = value.map((item) => item.id) ?? [];
                                getMeetingRoomList(1, filter, meetingRoomIds, 6, true).then(
                                    (res) => {
                                        if (res.success) {
                                            setMeetingRoomList(res.data?.meetingRoomList ?? []);
                                            setTotalPage(res.data?.totalPageNumber ?? 1);
                                            setCurrentPage(res.data?.nowPageNumber ?? 1);
                                        }
                                    }
                                );
                            }}
                            //TODO: meetingRoomList는 전체 회의실 리스트, orgFilterMeetingRoomList는 필터링 전의 전체 회의실 리스트
                            meetingRoomList={orgFilterMeetingRoomList}
                        />
                    </LocationFilterContainer>
                </FilterSection>
            </TopSection>
            <BottomSection
                style={{
                    padding: "0 20px",
                }}
            >
                {meetingRoomList.length === 0 && (
                    <EmptySection>
                        <UI.Icon
                            name="meetingRoom2"
                            size={24}
                            color="var(--alias-color-label-alternative)"
                        />
                        <UI.Typography
                            type="body01 normal"
                            fontStyle="regular"
                            color="var(--alias-color-label-normal)"
                        >
                            등록된 회의실이 없습니다. 새로운 회의실을 등록해 보세요!
                        </UI.Typography>
                    </EmptySection>
                )}
                <CardList>
                    {meetingRoomList.map((room, index) => (
                        <RoomManageCard
                            isBlocked={room.isBlocked}
                            room={room}
                            key={index}
                            onEdit={() => {
                                setSelectedRoom(room);
                                setOpenEditModal(true);
                            }}
                            onDelete={() => {
                                setSelectedRoom(room);
                                setOpenDeleteModal(true);
                            }}
                            isEditable={hasSuperAdminRoles}
                        />
                    ))}
                </CardList>
                <UI.Pagination
                    pageCount={totalPage}
                    currentPage={currentPage}
                    onChange={(page) => {
                        if (page < 1) return;

                        let meetingRoomIds = filteredMeetingRoomList.map((item) => item.id) ?? [];
                        getMeetingRoomList(page, filter, meetingRoomIds, 6, true).then((res) => {
                            if (res.success) {
                                setMeetingRoomList(res.data?.meetingRoomList ?? []);
                                setTotalPage(res.data?.totalPageNumber ?? 1);
                                setCurrentPage(res.data?.nowPageNumber ?? 1);
                            }
                        });
                    }}
                />
            </BottomSection>
            <GoogleCalendarModal
                googleRefreshToken={googleRefreshToken}
                googleCalendar={googleCalendar}
                modalIsOpen={openGoogleCalendarModal}
                onUpdate={() => getMeetingRooms()}
                closeModal={() => setOpenGoogleCalendarModal(false)}
                maxEnableCreateMeetingRoom={
                    (pricingPlan?.limitMeetingRoomNum ?? 1000) - totalSearchMeetingRoomCount
                }
            />
            <RoomRegisterModal
                onUpdate={() => {
                    let meetingRoomIds = filteredMeetingRoomList.map((item) => item.id) ?? [];
                    getMeetingRoomList(currentPage, filter, meetingRoomIds, 6, true).then((res) => {
                        if (res.success) {
                            setMeetingRoomList(res.data?.meetingRoomList ?? []);
                            setTotalPage(res.data?.totalPageNumber ?? 1);
                            setCurrentPage(res.data?.nowPageNumber ?? 1);
                        }
                    });
                    getMetaInfoMeetingRoom();
                }}
                modalIsOpen={openRoomCreateModal}
                closeModal={() => {
                    setOpenRoomCreateModal(false);
                }}
            />
            <RoomEditModal
                modalIsOpen={openEditModal}
                closeModal={() => setOpenEditModal(false)}
                room={selectedRoom}
                onUpdate={() => {
                    let meetingRoomIds = filteredMeetingRoomList.map((item) => item.id) ?? [];
                    getMeetingRoomList(currentPage, filter, meetingRoomIds, 6, true).then((res) => {
                        if (res.success) {
                            setMeetingRoomList(res.data?.meetingRoomList ?? []);
                            setTotalPage(res.data?.totalPageNumber ?? 1);
                            setCurrentPage(res.data?.nowPageNumber ?? 1);
                        }
                    });
                    getMetaInfoMeetingRoom();
                }}
            />
            <DeleteConfirmationModal
                isBlockedMeetingRoom={haveBlockedMeetingRoom}
                meetingRoomName={selectedRoom?.name ?? ""}
                modalIsOpen={openDeleteModal}
                closeModal={() => setOpenDeleteModal(false)}
                onDelete={() =>
                    onDeleteMeetingRoom(
                        selectedRoom?.id ?? "",
                        (selectedRoom?.meetingRoomIntegration &&
                            selectedRoom?.meetingRoomIntegration.integrationProvider ===
                                "GOOGLE") ||
                            false
                    )
                }
            />
            <LoadingModal
                modalIsOpen={loadingInfo?.loading}
                title={loadingInfo.message ?? ""}
                isShowWaitingText={loadingInfo.isShowWaitingText}
            />
            <RoomCreationLimitExceededModal
                limitOfUsageRooms={pricingPlan?.limitMeetingRoomNum ?? 1000}
                countOfUsageRooms={totalSearchMeetingRoomCount}
                plan={pricingPlan?.plan ?? undefined}
                modalIsOpen={openRoomCreationLimitExceededModal}
                closeModal={() => setOpenRoomCreationLimitExceededModal(false)}
            />
        </Container>
    );
};

export default MeetingRoomManagementPage;

const EmptySection = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`;
